import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';

const TeamPage = () => {
  const teamMembers = [
    {
      name: 'Pengyi Shi, PhD',
      title: 'Fouder & CEO',
      bio: '11+ years applying stochastic modeling and AI/ML to real-world healthcare operations and staffing, including a decade+ on Purdue\'s operations faculty (now Associate Professor at the Mitchell E. Daniels, Jr. School of Business).',
      image: '/media/images/team/pengyi-shi.jpeg',
      linkedin: 'https://www.linkedin.com/in/pengyi-shi-25b165145/',
    },
    {
      name: 'Rada Yovovich',
      title: 'Strategic Consultant & Board Advisor',
      bio: '19+ years driving health-tech and healthcare services innovation across consulting, operations, and strategy (ex-COO/Board Member at Medecipher; Chief of Staff/SVP Strategic Ops at SnapCare; co-founder of The Darkest Horse).',
      image: '/media/images/team/rada-yovovich.jpg',
      linkedin: 'https://www.linkedin.com/in/rada/',
    },
    {
      name: 'Rahul Sharma',
      title: 'GTM & Strategy',
      bio: '5+ years building and scaling tech startups (0→1→scale); drives DeltaOps’ go-to-market and strategy through customer discovery, outreach, and growth messaging.',
      image: '/media/images/team/rahul-sharma.png',
      linkedin: 'https://linkedin.com/in/alienavi',
    },
    {
      name: 'Minrui (Rey) L.',
      title: 'Developer',
      bio: '2+ years building backend systems and applied AI for healthcare operations, including developing DeltaOps\' Python-based decision-support infrastructure.',
      image: '/media/images/team/minrui-lu.jpg',
      linkedin: 'https://www.linkedin.com/in/minrui-lu/',
    },
    {
      name: 'Peter Xu',
      title: 'Developer',
      bio: '2+ years building cloud-scale software at Amazon/AWS, with strong Python + AWS chops and a track record of ML-driven analytics work (including hospital ward management modeling).',
      image: '/media/images/team/peter-xu.jpg',
      linkedin: 'https://linkedin.com',
    }
  ];

  return (
    <div className="team-page" data-testid="team-page">
      <Seo
        title="Team | DeltaOps AI Leadership"
        description="Meet the healthcare operators, AI researchers, and product leaders building DeltaOps AI."
        image="https://images.pexels.com/photos/19213884/pexels-photo-19213884.jpeg"
        imageAlt="Team collaboration in a modern office"
      />
      {/* Hero Section */}
      <section
        className="min-h-screen flex items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900/20 text-white"
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-5xl space-y-6">
            <p className="text-cyan-300 uppercase tracking-widest text-sm">Leadership</p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight" data-testid="page-title">
              Meet Our Team
            </h1>
            <p className="text-lg text-slate-200">
              A diverse mix of healthcare operators, AI researchers, and product experts who keep hospital and home health supply chains in sync. We build, test, and scale coordination intelligence every day.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="btn bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg transition hover:-translate-y-0.5"
              >
                Talk to Our Team <ArrowRight className="ml-2" />
              </Link>
              <Link
                to="/operations-flow"
                className="btn border border-white/20 px-6 py-3 rounded-lg text-white hover:text-cyan-300"
              >
                See the Operations Flow
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Team Introduction */}
      <section className="py-16 bg-[#020a17]" data-testid="intro-section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-white">Leadership Built on Experience</h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              Our leadership team brings together decades of experience in healthcare, artificial intelligence, and enterprise software. We're united by a common mission: to make healthcare operations more efficient, effective, and humane.
            </p>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 bg-slate-900" data-testid="team-grid-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white/5 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-white/10 backdrop-blur"
                data-testid={`team-member-${index}`}
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-1 text-white">{member.name}</h3>
                  <p className="text-cyan-300 font-semibold mb-3">{member.title}</p>
                  <p className="text-slate-300 mb-4 text-sm leading-relaxed">{member.bio}</p>
                  <div className="flex space-x-3">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/5 text-white rounded-lg border border-white/10 hover:bg-white/10 transition-colors"
                      aria-label={`${member.name} LinkedIn`}
                    >
                      <Linkedin size={20} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950" data-testid="join-section">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-white">Join Our Team</h2>
            <p className="text-xl text-slate-300 mb-8">
              We're always looking for talented individuals who share our passion for improving healthcare. If you're interested in making a real impact, we'd love to hear from you.
            </p>
            <a
              href="/contact"
              className="btn btn-primary text-lg px-8 py-4"
              data-testid="join-cta-button"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950 text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Work with Us?</h2>
          <p className="text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            Let's discuss how DeltaOps AI can transform your healtcare operations.
          </p>
          <a
            href="/contact"
            className="btn bg-white/10 text-white border border-white/30 hover:bg-white/20 text-lg px-8 py-4"
            data-testid="footer-cta-button"
          >
            Schedule a Demo
          </a>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
